import React, { useState, useEffect } from "react";
import {
    Dialog,
    IconButton,
    makeStyles,
    Button,
    TextField,
    Tabs,
    Tab,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import UploadIcon from "../../../images/upload-image.png";
import VideoIcon from "../../../images/upload-video.png";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import LinkIcon from "@material-ui/icons/Link";
import ChangeImageIcon from "../../../images/hugeicons_image-upload.png";
import TrashIcon from "../../../images/trash-icon-white.svg";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { useEnumStrings } from "../../enumStrings";
import DeleteBannerModal from "./DeleteBannerModal";
import theme from "../../../themes";
import { normalizeYouTubeVideoUrl } from "../../common/utilities/youtubeVideoUrl";

// Styles matching the provided design
const useStyles = makeStyles(() => ({
    dialogPaper: {
        borderRadius: "20px",
        padding: "0",
        maxWidth: "696px",
        width: "696px",
        minHeight: "681px",
        position: "relative",
        backgroundColor: theme.palette.background.paper,
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            maxWidth: "calc(100% - 24px)",
            minHeight: 0,
            maxHeight: "calc(100vh - 24px)",
            margin: "12px",
            borderRadius: "16px",
            overflowY: "auto",
        },
    },
    dialogCloseButton: {
        position: "absolute",
        right: "10px",
        top: "10px",
        backgroundColor: theme.palette.delete.main,
        color: theme.palette.delete.contrastText,
        width: "30px",
        height: "30px",
        zIndex: 10,
        borderRadius: "100px",
        "&:hover": {
            backgroundColor: theme.palette.delete.dark,
        },
        "& svg": {
            fontSize: "20px",
        },
    },
    title: {
        fontSize: "30px",
        fontWeight: 700,
        textAlign: "center",
        marginTop: "40px",
        fontFamily: "DM Sans, sans-serif",
        lineHeight: "150%",
        [theme.breakpoints.down("sm")]: {
            fontSize: "22px",
            marginTop: "28px",
        },
    },
    subTitle: {
        fontSize: "18px",
        fontWeight: 700,
        textAlign: "center",
        color: "#000",
        marginBottom: "20px",
        marginTop: "10px",
        maxWidth: "469px",
        marginLeft: "auto",
        marginRight: "auto",
        lineHeight: "150%",
        fontFamily: "DM Sans, sans-serif",
        [theme.breakpoints.down("sm")]: {
            fontSize: "14px",
            padding: "0 18px",
            marginBottom: "14px",
        },
    },
    tabAndContentWrapper: {
        width: "580px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "0px",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            padding: "0 16px",
            boxSizing: "border-box",
        },
    },
    tabAndContentWrapperSmall: {
        width: "539px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "0px",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            padding: "0 16px",
            boxSizing: "border-box",
        },
    },
    tabsContainer: {
        marginLeft: "36px",
        marginBottom: "-1px",
        position: "relative" as const,
        zIndex: 2,
        "& .MuiTabs-root": {
            minHeight: "40px",
        },
        "& .MuiTabs-indicator": {
            display: "none",
        },
        "& .MuiTabs-flexContainer": {
            gap: "0px",
        },
        [theme.breakpoints.down("sm")]: {
            marginLeft: "0px",
        },
    },
    tabsContainerNoOffset: {
        marginLeft: "0px",
        marginBottom: "-1px",
        position: "relative" as const,
        zIndex: 2,
        "& .MuiTabs-root": {
            minHeight: "40px",
        },
        "& .MuiTabs-indicator": {
            display: "none",
        },
        "& .MuiTabs-flexContainer": {
            gap: "0px",
        },
    },
    tab: {
        textTransform: "none" as const,
        fontSize: "20px",
        fontWeight: 600,
        fontFamily: "DM Sans, sans-serif",
        lineHeight: "24px",
        minWidth: "155px",
        width: "155px",
        height: "40px",
        minHeight: "40px",
        border: `1px dashed ${theme.palette.grey[500]}`,
        borderBottom: "none",
        borderTopLeftRadius: "10px",
        borderTopRightRadius: "10px",
        marginRight: "0px",
        opacity: 1,
        color: theme.palette.grey[500],
        [theme.breakpoints.down("sm")]: {
            fontSize: "15px",
            minWidth: 0,
            width: "50%",
            flex: 1,
        },
    },
    tabImageSelected: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: "none",
        borderBottom: "none",
    },
    tabVideoSelected: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.primary.contrastText,
        border: "none",
        borderBottom: "none",
    },
    contentBox: {
        border: `1px dashed ${theme.palette.grey[500]}`,
        borderRadius: "10px",
        width: "100%",
        height: "291px",
        marginBottom: "20px",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        backgroundColor: theme.palette.background.paper,
        boxSizing: "border-box",
        [theme.breakpoints.down("sm")]: {
            height: "220px",
            padding: "14px",
        },
    },
    contentBoxSmall: {
        border: `1px dashed ${theme.palette.grey[500]}`,
        borderRadius: "10px",
        width: "100%",
        height: "254px",
        marginBottom: "20px",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        backgroundColor: theme.palette.background.paper,
        boxSizing: "border-box",
        [theme.breakpoints.down("sm")]: {
            height: "auto",
            minHeight: "170px",
            padding: "10px",
            marginBottom: "14px",
        },
    },
    uploadIcon: {
        width: "80px",
        height: "80px",
        marginBottom: "10px",
        objectFit: "contain",
        [theme.breakpoints.down("sm")]: {
            width: "56px",
            height: "56px",
        },
    },
    playIcon: {
        width: "49px",
        height: "49px",
        color: theme.palette.common.black,
        marginBottom: "10px",
    },
    uploadText: {
        color: theme.palette.primary.main,
        fontSize: "20px",
        fontWeight: 600,
        fontFamily: "DM Sans, sans-serif",
        lineHeight: "24px",
        marginTop: "10px",
        cursor: "pointer",
        textDecoration: "underline",
        [theme.breakpoints.down("sm")]: {
            fontSize: "15px",
            lineHeight: "20px",
        },
    },
    footer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "8px 38px 20px 38px",
        [theme.breakpoints.down("sm")]: {
            padding: "8px 16px 20px 16px",
        },
    },
    footerText: {
        fontSize: "14px",
        fontWeight: 400,
        textAlign: "center",
        color: "#000",
        marginBottom: "12px",
        maxWidth: "500px",
        fontFamily: "DM Sans, sans-serif",
        lineHeight: "150%",
        [theme.breakpoints.down("sm")]: {
            fontSize: "13px",
        },
    },
    saveButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontSize: "15px",
        fontWeight: 600,
        fontFamily: "DM Sans, sans-serif",
        width: "170px",
        height: "42px",
        borderRadius: "10px",
        textTransform: "uppercase" as const,
        lineHeight: "1",
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        "&:hover": {
            backgroundColor: theme.palette.primary.dark,
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            maxWidth: "280px",
        },
    },
    warningBox: {
        border: `2px solid ${theme.palette.delete.main}`,
        color: theme.palette.delete.main,
        padding: "0px 10px",
        borderRadius: "4px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        fontSize: "18px",
        fontWeight: 600,
        width: "519px",
        height: "58px",
        fontFamily: "DM Sans, sans-serif",
        lineHeight: "150%",
        boxSizing: "border-box" as const,
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            maxWidth: "100%",
            height: "auto",
            padding: "8px 12px",
            fontSize: "14px",
        },
    },
    inputContainer: {
        width: "400px",
        marginTop: "8px",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            maxWidth: "100%",
        },
    },
    redirectLabel: {
        fontSize: "13px",
        fontWeight: 500,
        color: "#333333",
        marginBottom: "6px",
        fontFamily: "DM Sans, sans-serif",
        lineHeight: "20px",
    },
    urlInput: {
        width: "100%",
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            height: "40px",
            backgroundColor: theme.palette.background.paper,
            "& fieldset": {
                borderColor: theme.palette.grey[300],
            },
        },
        "& .MuiInputBase-input": {
            fontFamily: "DM Sans, sans-serif",
            fontSize: "16px",
            lineHeight: "24px",
            color: theme.palette.text.primary,
            "&::placeholder": {
                color: theme.palette.text.primary,
                opacity: 1,
            },
        },
    },
    urlInputGold: {
        width: "100%",
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            height: "40px",
            backgroundColor: theme.palette.background.paper,
            "& fieldset": {
                borderColor: theme.palette.primary.main,
            },
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
            },
        },
        "& .MuiInputBase-input": {
            fontFamily: "DM Sans, sans-serif",
            fontSize: "16px",
            lineHeight: "24px",
            color: theme.palette.text.primary,
            "&::placeholder": {
                color: theme.palette.text.primary,
                opacity: 1,
            },
        },
    },
    previewImage: {
        width: "409px",
        height: "213px",
        borderRadius: "4px",
        marginBottom: "10px",
        objectFit: "cover" as const,
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            maxWidth: "100%",
            height: "auto",
            maxHeight: "150px",
            objectFit: "contain" as const,
            marginBottom: "0",
        },
    },
    actionButtons: {
        display: "flex",
        gap: "15px",
        marginTop: "10px",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "stretch",
            gap: "10px",
            width: "100%",
        },
    },
    changeButton: {
        display: "flex",
        flexDirection: "row" as const,
        alignItems: "center",
        padding: "6px 16px",
        gap: "6px",
        width: "207px",
        minWidth: "207px",
        height: "42px",
        background: theme.palette.primary.main,
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        borderRadius: "10px",
        border: "none",
        fontFamily: "DM Sans, sans-serif",
        fontWeight: 600,
        fontSize: "15px",
        lineHeight: "1",
        color: theme.palette.primary.contrastText,
        textTransform: "uppercase" as const,
        boxSizing: "border-box" as const,
        whiteSpace: "nowrap" as const,
        justifyContent: "center",
        "&:hover": {
            background: theme.palette.primary.dark,
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            minWidth: 0,
        },
    },
    deleteButton: {
        display: "flex",
        flexDirection: "row" as const,
        alignItems: "center",
        padding: "6px 16px",
        gap: "6px",
        width: "195px",
        minWidth: "195px",
        height: "42px",
        background: theme.palette.delete.main,
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        borderRadius: "10px",
        border: "none",
        fontFamily: "DM Sans, sans-serif",
        fontWeight: 600,
        fontSize: "15px",
        boxSizing: "border-box" as const,
        lineHeight: "1",
        color: theme.palette.delete.contrastText,
        textTransform: "uppercase" as const,
        whiteSpace: "nowrap" as const,
        justifyContent: "center",
        "&:hover": {
            background: theme.palette.delete.dark,
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            minWidth: 0,
        },
    },
    postImageActions: {
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        [theme.breakpoints.down("sm")]: {
            padding: "0 16px",
            boxSizing: "border-box" as const,
            width: "100%",
        },
    },
}));

// CMP Accelerator Member Dashboard (2) texts. ACC:1172 repeats "and website
// directories and website directory"; the repeat is left out (design typo, asked Design).
export const MEMBER_DESIGN_TEXT = {
    quality: 'For best quality, we recommend an aspect ratio of 16:9 with a minimum size of 1280 × 720.',
    videoPlaceholder: 'Enter YouTube Video URL',
    footerLead: 'To make your changes visible in the banner on Chamber Perks App™ and website directories,',
};

interface BannerUploadModalProps {
    open: boolean;
    onClose: () => void;
    bannerIndex: number;
    useFeaturedMemberGuidance?: boolean;
    /** CMP member dashboard: CMP Accelerator Member Dashboard (2) texts (ACC:1116-1174). CPA pages keep theirs. */
    memberDesign?: boolean;
    initialData: {
        image?: string | null;
        videoUrl?: string | null;
        redirectUrl?: string | null;
    };
    onSave: (data: { image: string | null; videoUrl: string | null; redirectUrl: string | null }) => void;
}

const BannerUploadModal: React.FC<BannerUploadModalProps> = ({
    open,
    onClose,
    bannerIndex,
    useFeaturedMemberGuidance = false,
    memberDesign = false,
    initialData,
    onSave,
}) => {
    const classes = useStyles();
    const enumStrings = useEnumStrings();
    const [activeTab, setActiveTab] = useState(0); // 0 = Image, 1 = Video

    // Local state for form data
    const [image, setImage] = useState<string | null>(initialData.image || null);
    const [videoUrl, setVideoUrl] = useState<string>(initialData.videoUrl || "");
    const [videoUrlError, setVideoUrlError] = useState("");
    const [redirectUrl, setRedirectUrl] = useState<string>(initialData.redirectUrl || "");
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Sync with initialData when modal opens
    useEffect(() => {
        if (open) {
            setImage(initialData.image || null);
            setVideoUrl(initialData.videoUrl || "");
            setVideoUrlError("");
            setRedirectUrl(initialData.redirectUrl || "");
            // Determine active tab based on existing data
            if (initialData.videoUrl && !initialData.image) {
                setActiveTab(1);
            } else {
                setActiveTab(0);
            }
        }
    }, [open, initialData]);

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setActiveTab(newValue);
        setVideoUrlError("");
    };

    const compressImage = (file: File, maxWidth = 1920, maxHeight = 1080, quality = 0.8): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    let { width, height } = img;

                    if (width > maxWidth || height > maxHeight) {
                        const ratio = Math.min(maxWidth / width, maxHeight / height);
                        width = Math.round(width * ratio);
                        height = Math.round(height * ratio);
                    }

                    const canvas = document.createElement("canvas");
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        resolve(e.target?.result as string);
                        return;
                    }
                    ctx.drawImage(img, 0, 0, width, height);
                    const compressed = canvas.toDataURL("image/jpeg", quality);
                    resolve(compressed);
                };
                img.onerror = () => reject(new Error("Failed to load image"));
                img.src = e.target?.result as string;
            };
            reader.onerror = () => reject(new Error("Failed to read file"));
            reader.readAsDataURL(file);
        });
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            try {
                const compressed = await compressImage(file);
                setImage(compressed);
            } catch {
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (e.target?.result) {
                        setImage(e.target.result as string);
                    }
                };
                reader.readAsDataURL(file);
            }
        }
    };

    const handleRemoveImage = () => {
        setImage(null);
        setRedirectUrl("");
    };

    const handleSave = async () => {
        const trimmedVideoUrl = videoUrl.trim();
        const normalizedVideoUrl = trimmedVideoUrl
            ? normalizeYouTubeVideoUrl({ value: trimmedVideoUrl })
            : null;

        if (trimmedVideoUrl && !normalizedVideoUrl) {
            setActiveTab(1);
            setVideoUrlError(enumStrings.hoverTooltip.youtubeURL.trim());
            return;
        }

        setVideoUrlError("");
        if (normalizedVideoUrl) {
            setVideoUrl(normalizedVideoUrl);
        }

        await onSave({
            image: image,
            videoUrl: normalizedVideoUrl,
            redirectUrl: redirectUrl,
        });
    };

    const isImageUploaded = activeTab === 0 && !!image && !videoUrl;

    return (<>
        <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }}>
            <IconButton className={classes.dialogCloseButton} onClick={onClose}>
                <CloseIcon />
            </IconButton>

            <div className={classes.title} style={{ color: activeTab === 0 ? theme.palette.primary.main : theme.palette.common.black }}>
                {activeTab === 0 ? enumStrings.bannerUpload.uploadImageBanner : enumStrings.bannerUpload.uploadVideoBanner}
            </div>
            <div className={classes.subTitle}>
                {memberDesign
                    ? MEMBER_DESIGN_TEXT.quality
                    : useFeaturedMemberGuidance
                        ? enumStrings.common.featuredMemberBannerGuidance
                        : enumStrings.bannerUpload.qualityRecommendation}
            </div>

            <div className={isImageUploaded ? classes.tabAndContentWrapperSmall : classes.tabAndContentWrapper}>
                <div className={isImageUploaded ? classes.tabsContainerNoOffset : classes.tabsContainer}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        textColor="inherit"
                    >
                        <Tab label={enumStrings.bannerUpload.imageTab} className={`${classes.tab} ${activeTab === 0 ? classes.tabImageSelected : ""}`} />
                        <Tab label={enumStrings.bannerUpload.videoTab} className={`${classes.tab} ${activeTab === 1 ? classes.tabVideoSelected : ""}`} />
                    </Tabs>
                </div>

                <div className={isImageUploaded ? classes.contentBoxSmall : classes.contentBox}>
                {activeTab === 0 && (
                    <>
                        {/* Warning if video url exists */}
                        {videoUrl ? (
                            <div className={classes.warningBox}>
                                <ErrorOutlineIcon />
                                {enumStrings.bannerUpload.deleteVideoWarning}
                            </div>
                        ) : (
                            <>
                                {!image ? (
                                    <>
                                        <img src={UploadIcon} alt="Upload" className={classes.uploadIcon} />
                                        <input
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            id="raised-button-file"
                                            type="file"
                                            onChange={handleImageUpload}
                                        />
                                        <label htmlFor="raised-button-file">
                                            <div className={classes.uploadText}>{enumStrings.bannerUpload.uploadImage}</div>
                                        </label>
                                    </>
                                ) : (
                                    <img src={image} alt="Preview" className={classes.previewImage} />
                                )}
                            </>
                        )}
                    </>
                )}

                {activeTab === 1 && (
                    <>
                        {/* Warning if image exists */}
                        {image ? (
                            <div className={classes.warningBox}>
                                <ErrorOutlineIcon />
                                {enumStrings.bannerUpload.deleteImageWarning}
                            </div>
                        ) : (
                            <>
                                {videoUrl ? (
                                    <PlayCircleOutlineIcon className={classes.playIcon} />
                                ) : (
                                    <img src={VideoIcon} alt="Video" className={classes.uploadIcon} style={{ width: "118px", height: "109px", filter: "brightness(0)" }} />
                                )}
                                <div className={classes.uploadText} style={{ textDecoration: 'none', cursor: 'default', color: '#000000' }}>
                                    {videoUrl ? enumStrings.bannerUpload.watchVideo : enumStrings.bannerUpload.enterVideoLink}
                                </div>

                                <div className={classes.inputContainer}>
                                    <TextField
                                        placeholder={memberDesign ? MEMBER_DESIGN_TEXT.videoPlaceholder : enumStrings.bannerUpload.videoLinkPlaceholder}
                                        variant="outlined"
                                        className={videoUrl ? classes.urlInputGold : classes.urlInput}
                                        value={videoUrl}
                                        onChange={(e) => {
                                            setVideoUrl(e.target.value);
                                            setVideoUrlError("");
                                        }}
                                        error={Boolean(videoUrlError)}
                                        helperText={videoUrlError}
                                        InputProps={{
                                            startAdornment: <LinkIcon style={{ color: "#999", marginRight: "8px", transform: "rotate(-45deg)" }} />
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}
                </div>
            </div>

            {/* Redirect input and action buttons - outside the dashed content box */}
            {activeTab === 0 && image && !videoUrl && (
                <div className={classes.postImageActions}>
                    <div className={classes.inputContainer}>
                        <div className={classes.redirectLabel}>
                            {enumStrings.bannerUpload.whereRedirect}
                        </div>
                        <TextField
                            placeholder={enumStrings.bannerUpload.imageLink}
                            variant="outlined"
                            className={classes.urlInput}
                            value={redirectUrl}
                            onChange={(e) => setRedirectUrl(e.target.value)}
                            InputProps={{
                                startAdornment: <LinkIcon style={{ color: "#999", marginRight: "8px", transform: "rotate(-45deg)" }} />
                            }}
                        />
                    </div>

                    <div className={classes.actionButtons}>
                        <input
                            accept="image/*"
                            style={{ display: "none" }}
                            id="change-button-file"
                            type="file"
                            onChange={handleImageUpload}
                        />
                        <label htmlFor="change-button-file">
                            <Button component="span" className={classes.changeButton} startIcon={<img src={ChangeImageIcon} style={{ width: 24, height: 24, filter: "brightness(0) invert(1)" }} alt="" />}>
                                {enumStrings.bannerUpload.changeImage}
                            </Button>
                        </label>
                        <Button className={classes.deleteButton} startIcon={<img src={TrashIcon} style={{ width: 24, height: 24, filter: "brightness(0) invert(1)" }} alt="" />} onClick={() => setShowDeleteModal(true)}>
                            {enumStrings.bannerUpload.deleteImage}
                        </Button>
                    </div>
                </div>
            )}

            <div className={classes.footer}>
                <div className={classes.footerText}>
                    {memberDesign ? (
                        <>
                            {MEMBER_DESIGN_TEXT.footerLead}<br />
                            click <b>Save Banner</b> then click <b>Update</b> at the bottom of the page.
                        </>
                    ) : enumStrings.bannerUpload.footerText}
                </div>
                <Button className={classes.saveButton} onClick={handleSave} style={memberDesign ? { textTransform: 'none' } : undefined}>
                    {memberDesign ? 'Save Banner' : enumStrings.bannerUpload.saveBanner.toUpperCase()}
                </Button>
            </div>
        </Dialog>

        <DeleteBannerModal
            open={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onDelete={handleRemoveImage}
        />
    </>);
};

export default BannerUploadModal;
